import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider } from './providers/AuthProvider';
import { AppRouter } from './router';
import AdminApp from '../admin/AdminApp';
import ErrorBoundary from '../shared/ui/ErrorBoundary';
import { GlobalFX } from '../shared/ui/GlobalStyles';
import { setAuthErrorHandler } from '../shared/api/client';
import { initMetrikaIfAllowed, exposeMetrikaDebugTools } from '../shared/lib/metrika';
import CookieBanner from '../shared/ui/CookieBanner';
import { I18nProvider } from '../shared/config/i18n';
import publicSite from '../shared/config/publicSite';


function isAdminHost() {
  try {
    const host = String(window.location?.hostname || '').trim().toLowerCase();
    return host === 'admin.noesa.ai';
  } catch {
    return false;
  }
}


export default function App() {
  const adminMode = React.useMemo(() => isAdminHost(), []);
  const [queryClient] = React.useState(() => new QueryClient());

  React.useEffect(() => {
    if (adminMode) return;
    setAuthErrorHandler(() => {
      const p = window.location.pathname;
      const publicRoots = ['/welcome', '/ai-planner', '/pricing', '/code', '/login', '/privacy', '/terms', '/cookies', '/news'];
      if (!publicSite.resolvePublicRoute(p) && p !== '/' && !publicRoots.some((r) => p.startsWith(r))) {
        window.location.assign('/');
      }
    });
  }, [adminMode]);

  React.useEffect(() => {
    if (adminMode) return;
    initMetrikaIfAllowed();
    exposeMetrikaDebugTools();
  }, [adminMode]);

  if (adminMode) {
    return <AdminApp />;
  }

  return (
    <I18nProvider>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <GlobalFX />
            <CookieBanner />
            <AppRouter />
          </AuthProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </I18nProvider>
  );
}
